import React, { forwardRef, InputHTMLAttributes, useCallback } from "react"
import styled, { css, FlattenSimpleInterpolation } from "styled-components"

import { Color, LineHeight, RadiusSize, TextSize, Transition } from "~/consts/style-mixins"

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  /** @default "Medium" */
  size?: keyof typeof StyledBySize
  onChangeText?: (value: string) => void
  register?: any
  isError?: boolean
}

const Input = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { size = "Medium", onChangeText, register, isError, ...inputProps } = props

  const onChangeInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      inputProps.onChange?.(e)
      onChangeText?.(e.target.value)
    },
    [inputProps.onChange, onChangeText]
  )

  return (
    <StyledInput
      {...inputProps}
      ref={register ?? ref}
      styledBySize={StyledBySize[size]}
      styledByError={isError}
      styledByDisabled={inputProps.disabled}
      onChange={onChangeInput}
    />
  )
})

const StyledBySize = {
  Medium: css`
    padding: 10.5px 16px;
    font-size: ${TextSize.M}px;
    min-height: 48px;
  `,
  Small: css`
    padding: 8px;
    font-size: ${TextSize.S}px;
    min-height: 32px;
  `,
}

interface StyledProps {
  styledBySize: FlattenSimpleInterpolation
  styledByError?: boolean
  styledByDisabled?: boolean
}

const StyledInput = styled.input<StyledProps>`
  width: 100%;
  line-height: 100%;
  border: 1px solid ${Color.LightGray};
  border-radius: ${RadiusSize.Small}px;
  line-height: ${LineHeight.M}%;
  background-color: ${Color.White};
  ${Transition.Hover};
  &::placeholder {
    color: ${Color.LightGray};
  }
  &:hover {
    border: 1px solid ${Color.Gray};
  }
  &:focus {
    border: 1px solid ${Color.Primary};
  }

  ${({ styledBySize }) => styledBySize}

  ${({ styledByError }) =>
    styledByError &&
    css`
      border: 1px solid ${Color.Attention};
      background: ${Color.BackgroundAttention};
    `}

  ${({ styledByDisabled }) =>
    styledByDisabled &&
    css`
      border: 1px solid ${Color.BorderGray};
      background-color: ${Color.BackgroundGray};
      &:hover {
        cursor: not-allowed;
      }
    `}
`

export default Input
